import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from './ui/button';
import TenantLogo from './TenantLogo';
import NotificationBell from './NotificationBell';
import { 
  TrendingUp, 
  LayoutDashboard, 
  Trophy, 
  Briefcase, 
  LogOut,
  Menu,
  X,
  Calculator,
  Sparkles,
  Crown,
  BarChart3,
  Users,
  GraduationCap,
  Share2,
  School,
  Shield,
  ChevronDown,
  Gamepad2,
  Settings,
  Key,
  ShoppingCart,
  Award,
  Bell,
  Gift,
  Video,
  HelpCircle
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const ADMIN_EMAILS = ['philipreardon121@gmail.com'];

// Dropdown component
const NavDropdown = ({ label, icon: Icon, items, isActive, color = 'slate' }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const colorClasses = {
    slate: 'text-slate-300 hover:bg-slate-800 hover:text-white',
    blue: 'text-blue-400 hover:bg-blue-500/10',
    purple: 'text-purple-400 hover:bg-purple-500/10',
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
          isActive ? 'bg-emerald-600/20 text-emerald-400' : colorClasses[color]
        }`}
      >
        <Icon className="h-4 w-4" />
        <span>{label}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 min-w-[160px] z-50">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                item.isActive 
                  ? 'bg-emerald-600/20 text-emerald-400' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {item.badge && (
                <span className={`text-xs px-1.5 py-0.5 rounded ${item.badgeClass}`}>
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = ({ onAuthClick }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  const isAdmin = ADMIN_EMAILS.includes(user?.email);
  const isTeacher = user?.role === 'teacher' || user?.role === 'admin';
  const isSchoolAdmin = user?.role === 'school_admin' || user?.role === 'admin';
  const hasEduAccess = isTeacher || isSchoolAdmin;

  // Check EDU flags
  const simplifiedUI = user?.simplified_ui || false;
  const hideMonetization = user?.hide_monetization || false;

  // Tools menu items - filtered based on EDU mode
  const toolItems = [
    { to: '/charts', label: 'Charts', icon: BarChart3, isActive: isActive('/charts') },
    // Hide shop for students with hide_monetization
    ...(!hideMonetization ? [{ to: '/achievements', label: 'Achievements', icon: Award, isActive: isActive('/achievements') || isActive('/shop') }] : []),
    { to: '/creator', label: 'Creator Hub', icon: Video, isActive: isActive('/creator') },
    ...(!hideMonetization ? [{ to: '/affiliate', label: 'Affiliate', icon: Gift, isActive: isActive('/affiliate') }] : []),
    { to: '/developer', label: 'API', icon: Key, isActive: isActive('/developer') },
    ...(user?.is_pro && !hideMonetization ? [
      { 
        to: '/pro/analytics', 
        label: 'Analytics', 
        icon: BarChart3, 
        isActive: isActive('/pro/analytics'),
        badge: 'PRO',
        badgeClass: 'bg-purple-500/20 text-purple-400'
      },
      { 
        to: '/pro/alerts', 
        label: 'Alerts', 
        icon: Bell, 
        isActive: isActive('/pro/alerts'),
        badge: 'PRO',
        badgeClass: 'bg-purple-500/20 text-purple-400'
      }
    ] : []),
  ];

  // EDU menu items
  const eduItems = [
    ...(isTeacher ? [{ to: '/teacher', label: 'My Classes', icon: GraduationCap, isActive: isActive('/teacher') }] : []),
    ...(isSchoolAdmin ? [{ to: '/school-admin', label: 'School Admin', icon: School, isActive: isActive('/school-admin') }] : []),
  ];

  // Game items - hide Chips for simplified UI
  const gameItems = simplifiedUI ? [
    { to: '/scorecard', label: 'Scorecard', icon: Calculator, isActive: isActive('/scorecard') },
    { to: '/leaderboard', label: 'Leaderboard', icon: Trophy, isActive: isActive('/leaderboard') },
    { to: '/leagues', label: 'Leagues', icon: Users, isActive: isActive('/leagues') },
    { to: '/how-it-works', label: 'How Scoring Works', icon: HelpCircle, isActive: isActive('/how-it-works') },
  ] : [
    { to: '/scorecard', label: 'Scorecard', icon: Calculator, isActive: isActive('/scorecard') },
    { to: '/chips', label: 'Chips', icon: Sparkles, isActive: isActive('/chips') },
    { to: '/leaderboard', label: 'Leaderboard', icon: Trophy, isActive: isActive('/leaderboard') },
    { to: '/leagues', label: 'Leagues', icon: Users, isActive: isActive('/leagues') },
    { to: '/how-it-works', label: 'How Scoring Works', icon: HelpCircle, isActive: isActive('/how-it-works') },
  ];

  // All links for mobile menu - filtered based on EDU mode
  const allMobileLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/portfolio', label: 'Portfolio', icon: Briefcase },
    { to: '/scorecard', label: 'Scorecard', icon: Calculator },
    ...(!simplifiedUI ? [{ to: '/chips', label: 'Chips', icon: Sparkles }] : []),
    { to: '/leaderboard', label: 'Leaderboard', icon: Trophy },
    { to: '/leagues', label: 'Leagues', icon: Users },
    { to: '/how-it-works', label: 'How Scoring Works', icon: HelpCircle },
    { to: '/charts', label: 'Charts', icon: BarChart3 },
    ...(!hideMonetization ? [{ to: '/achievements', label: 'Achievements', icon: Award }] : []),
    { to: '/creator', label: 'Creator Hub', icon: Video },
    { to: '/affiliate', label: 'Affiliate', icon: Gift },
    ...(user?.is_pro && !hideMonetization ? [
      { to: '/pro/analytics', label: 'Analytics', icon: BarChart3 },
      { to: '/pro/alerts', label: 'Alerts', icon: Bell }
    ] : []),
    ...(isTeacher ? [{ to: '/teacher', label: 'My Classes', icon: GraduationCap }] : []),
    ...(isSchoolAdmin ? [{ to: '/school-admin', label: 'School Admin', icon: School }] : []),
    ...(isAdmin ? [{ to: '/admin', label: 'Admin', icon: Shield }] : []),
    { to: '/settings', label: 'Settings', icon: Settings },
    ...(!user?.is_pro && !hideMonetization ? [{ to: '/subscription', label: 'Upgrade to Pro', icon: Crown }] : []),
  ];

  const isGameActive = ['/scorecard', '/chips', '/leaderboard', '/leagues'].includes(location.pathname);
  const isToolsActive = ['/charts', '/creator', '/affiliate', '/pro/analytics', '/pro/alerts', '/achievements', '/developer'].includes(location.pathname);
  const isEduActive = ['/teacher', '/school-admin'].includes(location.pathname);

  return (
    <nav 
      className="bg-slate-900/95 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-40" 
      data-testid="navbar"
      style={{ 
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo - Tenant aware */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <TenantLogo size="sm" showText={true} />
          </Link>

          {/* Desktop Nav */}
          {isAuthenticated && (
            <div className="hidden lg:flex items-center gap-1">
              {/* Main Links */}
              <Link
                to="/dashboard"
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
                  isActive('/dashboard') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Link>
              
              <Link
                to="/portfolio"
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
                  isActive('/portfolio') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Briefcase className="h-4 w-4" />
                Portfolio
              </Link>

              {/* Game Dropdown */}
              <NavDropdown 
                label="Game" 
                icon={Gamepad2} 
                items={gameItems} 
                isActive={isGameActive}
              />

              {/* Tools Dropdown */}
              <NavDropdown 
                label="Tools" 
                icon={Settings} 
                items={toolItems} 
                isActive={isToolsActive}
              />

              {/* EDU Dropdown - only show if user has EDU access */}
              {hasEduAccess && (
                <NavDropdown 
                  label="EDU" 
                  icon={GraduationCap} 
                  items={eduItems} 
                  isActive={isEduActive}
                  color="blue"
                />
              )}

              {/* Admin Link */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
                    isActive('/admin') ? 'bg-emerald-600/20 text-emerald-400' : 'text-red-400 hover:bg-red-500/10'
                  }`}
                >
                  <Shield className="h-4 w-4" />
                  Admin
                </Link>
              )}

              {/* Pro Upgrade - only show if not pro */}
              {!user?.is_pro && (
                <Link
                  to="/subscription"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-yellow-400 hover:bg-yellow-500/10 transition-colors"
                >
                  <Crown className="h-4 w-4" />
                  Pro
                </Link>
              )}
            </div>
          )}

          {/* Right side */}
          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <>
                <div className="hidden sm:flex items-center gap-2 text-sm bg-slate-800/50 px-3 py-1.5 rounded-lg">
                  <span className="text-slate-400">V$</span>
                  <span className="text-emerald-400 font-semibold" data-testid="nav-balance">
                    {user?.virtual_cash?.toLocaleString() || '100,000'}
                  </span>
                </div>
                
                {/* Notification Bell */}
                <NotificationBell />
                
                {/* Settings link */}
                <Link
                  to="/settings"
                  className={`p-2 rounded-lg transition-colors ${
                    isActive('/settings') ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="nav-settings-btn"
                  title="Settings"
                >
                  <Settings className="h-4 w-4" />
                </Link>
                
                <div className="hidden md:block text-slate-300 text-sm font-medium" data-testid="nav-username">
                  {user?.username}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={logout}
                  className="text-slate-400 hover:text-red-400 p-2"
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button 
                onClick={onAuthClick}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="nav-login-btn"
              >
                Get Started
              </Button>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden text-slate-300 p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && isAuthenticated && (
        <div 
          className="lg:hidden bg-slate-800 border-t border-slate-700" 
          data-testid="mobile-menu"
          style={{ 
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}
        >
          <div className="px-4 py-3 space-y-1 max-h-[70vh] overflow-y-auto">
            {allMobileLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                  isActive(link.to)
                    ? 'bg-emerald-600/20 text-emerald-400'
                    : 'text-slate-300 hover:bg-slate-700'
                }`}
              >
                <link.icon className="h-5 w-5" />
                {link.label}
              </Link>
            ))}
            <div className="pt-3 mt-2 border-t border-slate-700">
              <div className="flex items-center justify-between px-4 py-2">
                <span className="text-slate-400">Balance:</span>
                <span className="text-emerald-400 font-semibold">V${user?.virtual_cash?.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
